// EmptyState — no-data placeholder (PSD §10.7). Presentation.
//
// Onboarding-aware (CRITIQUE.md Priority 4): every empty state carries a lucide
// icon (no unicode glyphs), a one-line *what*, an optional one-line *why*
// (domain-reassuring microcopy in the same expert voice as HeadlineMetrics
// tooltips), and a clear next action. First-run empty states surface the
// Load-Demo path via <LoadDemoButton>.

import type { ReactNode } from 'react';
import { Inbox } from 'lucide-react';

interface EmptyStateProps {
  /** Decorative icon. Defaults to a muted lucide Inbox. Pass a lucide icon
   *  chosen for semantic fit with the empty surface. */
  icon?: ReactNode;
  /** Primary one-line statement of what is missing (the "what"). */
  message: string;
  /** One-line domain-reassuring explanation of *why* this is empty and what
   *  appears here once populated (the "why"). Expert, non-condescending. */
  hint?: string;
  /** Optional next-action affordance — a single button or a small button row. */
  action?: ReactNode;
}

export function EmptyState({ icon, message, hint, action }: EmptyStateProps) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon" aria-hidden="true">
        {icon ?? <Inbox size={32} strokeWidth={1.5} />}
      </div>
      <p className="empty-state-message">{message}</p>
      {hint && <p className="empty-state-hint">{hint}</p>}
      {action && <div className="empty-state-action">{action}</div>}
    </div>
  );
}
